import { OktaAuth } from '@okta/okta-auth-js'
import axios from 'axios'
import { config } from '../Config'

// Assigned after authClient exists. transformAuthState only runs after start(),
// which is after this module has finished evaluating.
let ensureFreshAccessToken = async () => undefined

const authClient = new OktaAuth({
    issuer: config.OktaIssuer,
    clientId: config.OktaClientID,
    redirectUri: window.location.origin,
    pkce: true,
    // offline_access is required for Okta to issue a refresh token alongside the access token
    scopes: ['openid', 'profile', 'email', 'offline_access'],
    // Renew via the refresh_token grant instead of the hidden-iframe silent renew, so renewal
    // keeps working even when third-party/iframe cookies are blocked or the Okta SSO session has lapsed
    useRefreshTokens: true,
    tokenManager: {
        autoRenew: true,
        // renew a bit before actual expiry so in-flight API calls don't race an expiring token
        expireEarlySeconds: 300
    },
    // https://github.com/okta/okta-auth-js#transformauthstate
    transformAuthState: async (oktaAuth, authState) => {
        if (!authState.isAuthenticated) {
          return authState
        }

        try {
          // Chrome can freeze background tabs so autoRenew never fires. Refresh first
          // or /userinfo runs with an expired JWT, isAuthenticated becomes false, and
          // every entry point redirects to login.
          await ensureFreshAccessToken()
          const user = await oktaAuth.token.getUserInfo()
          authState.isAuthenticated = !!user
          authState.users = user
        } catch (error) {
          console.error(error)
          authState.isAuthenticated = false
        }
        return authState
      }
})

function applyAccessTokenToAxios (accessToken) {
    if (accessToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }
}

let freshAccessTokenPromise = null

/**
 * Return a current access token, renewing with the refresh token when the
 * access token is missing or inside the expire-early window.
 * Concurrent callers share one in-flight renew so two tabs-worth of API
 * calls cannot rotate the same refresh token twice.
 */
ensureFreshAccessToken = async function ensureFreshAccessToken () {
    if (freshAccessTokenPromise) {
        return freshAccessTokenPromise
    }

    freshAccessTokenPromise = (async () => {
        try {
            let token = await authClient.tokenManager.get('accessToken')
            if (token && authClient.tokenManager.hasExpired(token)) {
                token = await authClient.tokenManager.renew('accessToken')
            }
            if (token?.accessToken) {
                applyAccessTokenToAxios(token.accessToken)
                return token.accessToken
            }
        } catch (error) {
            console.error(error)
        }
        return undefined
    })().finally(() => {
        freshAccessTokenPromise = null
    })

    return freshAccessTokenPromise
}

// Every entry point sets the shared axios Authorization header once at load time from
// authClient.getAccessToken(). Without this, a background renewal (silent or via refresh
// token) never reaches axios: the next API call sends the now-stale token, gets a 401, and
// the app's 401 interceptor signs the user out even though their session is still valid.
authClient.tokenManager.on('renewed', (key, newToken) => {
    if (key === 'accessToken' && newToken?.accessToken) {
        applyAccessTokenToAxios(newToken.accessToken)
    }
})

// Read (and renew if needed) the access token on every request so a Chrome tab that
// was frozen or discarded cannot send the snapshot JWT from page load.
axios.interceptors.request.use(async (request) => {
    const accessToken = await ensureFreshAccessToken()
    if (accessToken) {
        request.headers.Authorization = `Bearer ${accessToken}`
    }
    return request
})

function renewIfTabBecameVisible () {
    if (document.visibilityState === 'visible') {
        ensureFreshAccessToken()
    }
}

document.addEventListener('visibilitychange', renewIfTabBecameVisible)
window.addEventListener('pageshow', (event) => {
    if (event.persisted || document.visibilityState === 'visible') {
        ensureFreshAccessToken()
    }
})

export { ensureFreshAccessToken }
export default authClient
